



























































import { AsyncComponentFactory, AsyncComponentPromise } from 'vue/types/options'
import { Component, Vue, Inject, Prop, Watch } from 'vue-property-decorator'
import { startCase } from 'lodash'
import { TweenMax, TimelineMax, Power4 } from 'gsap'
import { Route } from 'vue-router'
import { Slide } from '@/@types/Slide'
import LoadingTemplate from '@/components/LoadingTemplate.vue'
import ErrorTemplate from '@/components/ErrorTemplate.vue'
import TrackerContainer from '@/components/TrackerContainer.vue'
import Watermark from '@/components/Watermark.vue'
import LinkIcon from '@/components/LinkIcon.vue'

@Component({
  components: {
    TrackerContainer,
    LinkIcon,
    Watermark
  },
  async beforeRouteEnter(to, from, next) {
    const { default: data } = await import(`@/assets/${to.params.project}`)
    next((vm: Presentation) => (vm.slides = data))
  }
})
export default class Presentation extends Vue {
  @Prop({ default: () => [1] }) public path: number[]

  /**
   * All slides.
   */
  private slides: Slide[] = null

  /**
   * Router transition.
   */
  private transition: string = 'none'

  /**
   * Transition duraction (in seconds).
   */
  private transitionDuration: number = 1

  /**
   * Presentation mode active.
   */
  private presentationMode: boolean = false

  /**
   * Presentation path index.
   */
  private presentationPathIndex: number = 0

  /**
   * Whether the presentation path index was updated.
   */
  private updatedPresentationPathIndex: boolean = false

  /**
   * Get presentation path.
   */
  get presentationPath() {
    return require(`@/assets/${this.$route.params.project}-path.json`)
  }

  /**
   * Get current index in presentation path.
   */
  @Watch('$route', { immediate: true })
  public setPresentationPathIndex(value) {
    const index = this.presentationPath.findIndex(path => {
      return (
        path.length === this.path.length &&
        path.every(
          (p, i) =>
            typeof this.path[i] !== 'undefined' && p === this.path[i] + 1
        )
      )
    })

    if (index > -1) {
      this.updatedPresentationPathIndex = true
      this.presentationPathIndex = index
    } else {
      this.updatedPresentationPathIndex = false
    }
  }

  /**
   * Get the next link.
   */
  get next(): string | null {
    return this.presentationMode
      ? this.presentationPathIndex < this.presentationPath.length - 1
        ? '/' +
          this.presentationPath[
            this.presentationPathIndex +
              (this.updatedPresentationPathIndex ? 1 : 0)
          ].join('/')
        : null
      : this.makePath('next')
  }

  /**
   * Get the previous link.
   */
  get previous(): string | null {
    return this.presentationMode
      ? this.presentationPathIndex
        ? '/' +
          this.presentationPath[
            this.presentationPathIndex -
              (this.updatedPresentationPathIndex ? 1 : 0)
          ].join('/')
        : null
      : this.makePath('previous')
  }

  /**
   * Get the zoomIn link.
   */
  get zoomIn(): string | null {
    return this.makePath('zoomIn')
  }

  /**
   * Get the zoomOut link.
   */
  get zoomOut(): string | null {
    return this.makePath('zoomOut')
  }

  /**
   * Get the current slide.
   */
  get slide(): Slide {
    return this.path.reduce((slide, index) => slide.slides[index], this as any)
  }

  /**
   * Get the parent slide.
   */
  get parentSlide(): Slide {
    let slide = this.slides[this.path[0]]
    const path = this.path.slice(1, this.path.length - 1)
    for (let i in path) {
      slide = slide.slides[path[i]]
    }
    return slide
  }

  /**
   * Get the template component.
   */
  get template(): AsyncComponentFactory {
    const name = startCase(this.slide.template || 'default').replace(/ /g, '')
    const component = (import(
      /* webpackChunkName: "template-[request]" */ `@/components/Templates/${name}`
    ) as any) as AsyncComponentPromise

    return () => ({
      component: component,
      loading: LoadingTemplate,
      error: ErrorTemplate
    })
  }

  /**
   * Make path.
   */
  public makePath(direction: 'next' | 'previous' | 'zoomIn' | 'zoomOut') {
    let path = null

    if (direction === 'next' && this.path[0] < this.slides.length - 1) {
      path = [this.path[0] + 2]
    } else if (direction === 'previous' && this.path[0] > 0) {
      path = [this.path[0]]
    } else if (
      direction === 'zoomIn' &&
      this.slide &&
      Array.isArray(this.slide.slides)
    ) {
      path = [...this.path.map(i => i + 1), 1]
    } else if (
      direction === 'zoomIn' &&
      this.path.length > 1 &&
      this.path[this.path.length - 1] < this.parentSlide.slides.length - 1
    ) {
      path = [
        ...this.path.slice(0, this.path.length - 1).map(i => i + 1),
        this.path[this.path.length - 1] + 2
      ]
    } else if (
      direction === 'zoomIn' &&
      this.path.length > 2 &&
      this.path[this.path.length - 1] === this.parentSlide.slides.length - 1
    ) {
      path = [
        ...this.path.slice(0, this.path.length - 2).map(i => i + 1),
        this.path[this.path.length - 2] + 2
      ]

      if (
        !path.reduce(
          (f, c, i, a) => (i < a.length - 1 ? f[c - 1].slides : f[c - 1]),
          this.slides
        )
      ) {
        path = null
      }
    } else if (
      direction === 'zoomOut' &&
      this.path.length > 1 &&
      this.path[this.path.length - 1] > 0
    ) {
      path = [
        ...this.path.slice(0, this.path.length - 1).map(i => i + 1),
        this.path[this.path.length - 1]
      ]
    } else if (direction === 'zoomOut' && this.path.length > 1) {
      path = this.path.slice(0, this.path.length - 1).map(i => i + 1)
    }

    return Array.isArray(path) ? `/${path.join('/').replace(/\/$/, '')}` : path
  }

  /**
   * Handle the keyDown event.
   */
  public onKeydown({ keyCode }: KeyboardEvent) {
    let route = `/${this.$route.params.project}`

    if ((keyCode === 39 || keyCode === 34) && this.next) {
      this.$router.push(route + this.next)
    } else if ((keyCode === 37 || keyCode === 33) && this.previous) {
      this.$router.push(route + this.previous)
    } else if (!this.presentationMode && keyCode === 40 && this.zoomIn) {
      this.$router.push(route + this.zoomIn)
    } else if (!this.presentationMode && keyCode === 38 && this.zoomOut) {
      this.$router.push(route + this.zoomOut)
    }
  }

  /**
   * Enter transition.
   */
  public enter(el, done) {
    if (this.transition !== 'none') {
      const tl = new TimelineMax({
        onComplete: done
      })

      const end = {
        x: 0,
        y: 0,
        scale: 1,
        transformOrigin: '50% 50%',
        ease: Power4.easeOut
      } as any

      const start = {
        transformOrigin: '50% 50%',
        ease: Power4.easeOut
      } as any

      tl.set(el, start)

      if (this.transition === 'previous') {
        start.scale = 0.8
        start.x = this.$el.clientWidth * 1.15 * -1
      } else if (this.transition === 'next') {
        start.scale = 0.8
        start.x = this.$el.clientWidth * 1.15
      } else if (this.transition === 'zoomIn') {
        start.y = this.$el.clientHeight
      } else if (this.transition === 'zoomOut') {
        start.y = this.$el.clientHeight * -1
      }

      tl.to(el, this.transitionDuration, end)
    } else {
      done()
    }
  }

  /**
   * Leave transition.
   */
  public leave(el, done) {
    if (this.transition !== 'none') {
      const tl = new TimelineMax({
        onComplete: done
      })

      const start = {
        scale: 1,
        x: 0,
        y: 0,
        transformOrigin: '50% 50%',
        ease: Power4.easeOut
      } as any

      const end = {
        transformOrigin: '50% 50%',
        ease: Power4.easeOut
      } as any

      tl.set(el, start)

      if (this.transition === 'previous') {
        end.scale = 0.8
        end.x = this.$el.clientWidth * 1.15
      } else if (this.transition === 'next') {
        end.x = this.$el.clientWidth * 1.15 * -1
        end.scale = 0.8
      } else if (this.transition === 'zoomIn') {
        end.y = this.$el.clientHeight * -1
      } else if (this.transition === 'zoomOut') {
        end.y = 0
      }

      tl.to(el, this.transitionDuration, end)
    } else {
      done()
    }
  }

  /**
   * Before route update.
   */
  public beforeRouteUpdate(to, from, next) {
    const path = to.path
      .replace(/\/$/, '')
      .split('/')
      .slice(2)
      .map(index => Number(index) - 1)

    const toIndex = path[0]
    const fromIndex = this.path[0]
    const maxLength = Math.max(path.length, this.path.length) - 1
    const toSubIndex = path[maxLength]
    const fromSubIndex = this.path[maxLength]

    if (toIndex > fromIndex) {
      this.transition = 'next'
    } else if (toIndex < fromIndex) {
      this.transition = 'previous'
    } else if (
      typeof fromSubIndex === 'undefined' ||
      toSubIndex > fromSubIndex
    ) {
      this.transition = 'zoomIn'
    } else if (typeof toSubIndex === 'undefined' || toSubIndex < fromSubIndex) {
      this.transition = 'zoomOut'
    }

    next()
  }

  /**
   * Swicth to presentation mode.
   */
  public switchToPresentation() {
    const elem = this.presentationMode
      ? (document as any)
      : (document.body as any)

    if (!this.presentationMode) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen()
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen()
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen()
      }
      this.presentationMode = true
    } else {
      if (elem.exitFullscreen) {
        elem.exitFullscreen()
      } else if (elem.mozExitFullscreen) {
        elem.mozExitFullscreen()
      } else if (elem.webkitExitFullscreen) {
        elem.webkitExitFullscreen()
      } else if (elem.msExitFullscreen) {
        elem.msExitFullscreen()
      }
      this.presentationMode = false
    }
  }

  /**
   * Destroy keydown event.
   */
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  }

  /**
   * Create keydown event.
   */
  created() {
    window.addEventListener('keydown', this.onKeydown)

    window.addEventListener('resize', () => {
      if (
        (screen.width === window.innerWidth &&
          screen.height === window.innerHeight) ||
        //@ts-ignore
        (document.fullScreen ||
          //@ts-ignore
          document.mozFullScreen ||
          //@ts-ignore
          document.webkitIsFullScreen)
      ) {
        this.presentationMode = true
      } else {
        this.presentationMode = false
      }
    })
  }
}
